import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Tabs,
  Tab,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

/**
 * Local Garment Picker
 *
 * Allows users to browse and select garment mockups from local Image_Assets folder
 * instead of relying on SS Activewear API integration.
 *
 * Directory structure:
 * /Image_Assets/tshirt Assets/{styleNumber}/{styleNumber}_{STYLE_NAME}_{COLOR}.jpg
 * /Image_Assets/tshirt Assets/{styleNumber}/BACKSHOTS/{styleNumber}_{STYLE_NAME}_{COLOR}_BACK.jpg
 */

// Garment catalog - organized by style number
const GARMENT_CATALOG = [
  {
    styleNumber: '5026',
    styleName: 'Classic Tee',
    category: 'T-Shirt',
    colors: [
      'ARMY', 'ATLANTIC', 'AUTUMN', 'BERRY', 'BLACK', 'BONE', 'BRIGHT_ROYAL',
      'BURGUNDY', 'BUTTER', 'CAMEL', 'CARDINAL', 'CHARCOAL', 'CHARLOTTE',
      'CHESTNUT', 'CLAY', 'COAL', 'COBALT', 'COCOA', 'COFFEE', 'CYPRESS',
      'ECRU', 'EMERALD', 'EUCALYPTUS', 'GRAVEL', 'GREY_MARLE', 'HAZY_PINK'
    ],
  },
  {
    styleNumber: '5026T',
    styleName: 'Classic Tear Out Tee',
    category: 'T-Shirt',
    colors: ['BLACK', 'ECRU', 'GREY_MARLE', 'WHITE'],
  },
  {
    styleNumber: '5030',
    styleName: 'Box Tee',
    category: 'T-Shirt',
    colors: ['BLACK', 'ECRU', 'FOREST_GREEN', 'INK_BLUE', 'KHAKI', 'MUSHROOM', 'SHADOW', 'STORM', 'WALNUT', 'WHITE'],
  },
  {
    styleNumber: '5040',
    styleName: 'Stone Wash Staple Tee',
    category: 'T-Shirt',
    colors: ['ASH_STONE', 'BLACK_STONE', 'BLUE_STONE', 'MOSS_STONE', 'ORCHID_STONE'],
  },
  {
    styleNumber: '5069',
    styleName: 'Classic Oversized Tee',
    category: 'T-Shirt',
    colors: ['BLACK', 'BONE', 'COAL', 'ECRU', 'NAVY', 'WHITE'],
  },
  {
    styleNumber: '5085',
    styleName: '', // No product name in filename - just 5085_{COLOR}.jpg
    category: 'T-Shirt',
    colors: ['BLACK_STONE', 'COAL_STONE', 'GREY_STONE'],
  },
  {
    styleNumber: '5086',
    styleName: 'Heavy Faded Minus Tee',
    category: 'T-Shirt',
    colors: ['FADED_BLACK', 'FADED_BONE', 'FADED_GREY'],
  },
  {
    styleNumber: '5150',
    styleName: 'Made Crew',
    category: 'Sweatshirt',
    colors: ['BLACK', 'GREY_MARLE', 'INK_BLUE'],
  },
  {
    styleNumber: '5151',
    styleName: 'Made Hood',
    category: 'Sweatshirt',
    colors: ['BLACK', 'GREY_MARLE'],
  },
  {
    styleNumber: '5165',
    styleName: 'Faded Relax Crew',
    category: 'Sweatshirt',
    colors: ['FADED_BLACK', 'FADED_BONE', 'FADED_GREY'],
  },
  {
    styleNumber: '5512',
    styleName: 'Felt Bomber Jacket',
    category: 'Jacket',
    colors: ['BLACK'],
  },
];

const LocalGarmentPicker = ({ open, onClose, onSelect }) => {
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Get unique categories
  const categories = ['all', ...new Set(GARMENT_CATALOG.map(g => g.category))];

  // Filter garments
  const filteredGarments = GARMENT_CATALOG.filter(garment => {
    const matchesCategory = categoryFilter === 'all' || garment.category === categoryFilter;
    const matchesSearch = searchQuery === '' ||
      garment.styleName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      garment.styleNumber.includes(searchQuery);
    return matchesCategory && matchesSearch;
  });

  // Construct image URLs
  const getImageUrl = (styleNumber, styleName, color, view = 'front') => {
    const baseUrl = '/Image_Assets/tshirtAssets';

    // Handle case where styleName is empty (e.g., 5085 uses just styleNumber_color.jpg)
    const styleNamePart = styleName ? `${styleName.toUpperCase().replace(/\s+/g, '_')}_` : '';

    let imageUrl;
    if (view === 'back') {
      imageUrl = `${baseUrl}/${styleNumber}/BACKSHOTS/${styleNumber}_${styleNamePart}${color}_BACK.jpg`;
    } else {
      imageUrl = `${baseUrl}/${styleNumber}/${styleNumber}_${styleNamePart}${color}.jpg`;
    }

    console.log('🖼️ Constructed image URL:', imageUrl);
    return imageUrl;
  };

  const handleSelectGarment = () => {
    if (!selectedStyle || !selectedColor) return;

    const frontImageUrl = getImageUrl(
      selectedStyle.styleNumber,
      selectedStyle.styleName,
      selectedColor,
      'front'
    );

    const backImageUrl = getImageUrl(
      selectedStyle.styleNumber,
      selectedStyle.styleName,
      selectedColor,
      'back'
    );

    onSelect({
      styleNumber: selectedStyle.styleNumber,
      styleName: selectedStyle.styleName,
      color: selectedColor,
      category: selectedStyle.category,
      mockupImages: {
        front: frontImageUrl,
        back: backImageUrl,
      },
    });

    // Reset and close
    setSelectedStyle(null);
    setSelectedColor(null);
    onClose();
  };

  const formatColorName = (color) => {
    return color
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Select Garment</Typography>
          {selectedStyle && (
            <Chip
              label={`${selectedStyle.styleName} (${selectedStyle.styleNumber})`}
              color="primary"
              onDelete={() => {
                setSelectedStyle(null);
                setSelectedColor(null);
              }}
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Search and Filter */}
        <Box mb={3}>
          <TextField
            fullWidth
            placeholder="Search by style name or number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Tabs
            value={categoryFilter}
            onChange={(e, newValue) => setCategoryFilter(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {categories.map(category => (
              <Tab
                key={category}
                label={category.charAt(0).toUpperCase() + category.slice(1)}
                value={category}
              />
            ))}
          </Tabs>
        </Box>

        {/* Step 1: Select Style */}
        {!selectedStyle && (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Step 1: Select a Style
            </Typography>
            <Grid container spacing={2}>
              {filteredGarments.map((garment) => (
                <Grid item xs={12} sm={6} md={4} key={garment.styleNumber}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid transparent',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => setSelectedStyle(garment)}
                  >
                    <CardMedia
                      component="img"
                      height="250"
                      image={getImageUrl(
                        garment.styleNumber,
                        garment.styleName,
                        garment.colors[0]
                      )}
                      alt={garment.styleName}
                      sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5', p: 2 }}
                      onError={(e) => {
                        console.error('Failed to load image:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {garment.styleName || `Style ${garment.styleNumber}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        #{garment.styleNumber}
                      </Typography>
                      <Chip
                        label={garment.category}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        {garment.colors.length} colors available
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Step 2: Select Color */}
        {selectedStyle && (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Step 2: Select a Color
            </Typography>
            <Grid container spacing={2}>
              {selectedStyle.colors.map((color) => (
                <Grid item xs={6} sm={4} md={3} key={color}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: selectedColor === color ? 'primary.main' : 'transparent',
                      position: 'relative',
                      '&:hover': {
                        borderColor: 'primary.light',
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => setSelectedColor(color)}
                  >
                    {selectedColor === color && (
                      <CheckCircleIcon
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          backgroundColor: 'white',
                          borderRadius: '50%',
                        }}
                      />
                    )}
                    <CardMedia
                      component="img"
                      height="200"
                      image={getImageUrl(
                        selectedStyle.styleNumber,
                        selectedStyle.styleName,
                        color
                      )}
                      alt={color}
                      sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5', p: 1 }}
                      onError={(e) => {
                        console.error('Failed to load color image:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                    <CardContent sx={{ py: 1 }}>
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{ fontWeight: selectedColor === color ? 'bold' : 'normal' }}
                      >
                        {formatColorName(color)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {selectedStyle && (
          <Button onClick={() => {
            setSelectedStyle(null);
            setSelectedColor(null);
          }}>
            Back to Styles
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSelectGarment}
          disabled={!selectedStyle || !selectedColor}
        >
          Select Garment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocalGarmentPicker;
